import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { X, CheckCircle2, Copy, Sparkles, BadgeCheck, Link2 } from "lucide-react";
import { toast } from "sonner";
import { formatCurrency, formatDate, truncHash } from "@/lib/api";

const Row = ({ label, value, mono, testid }) => (
  <div className="flex items-center justify-between py-2.5 border-b border-gray-100 last:border-0">
    <span className="text-[11px] font-semibold tracking-wider uppercase text-gray-500">
      {label}
    </span>
    <span
      data-testid={testid}
      className={`text-sm text-gray-900 ${mono ? "font-mono" : ""}`}
    >
      {value}
    </span>
  </div>
);

export default function MintedDialog({ open, receipt, onClose }) {
  const [copied, setCopied] = useState(null);
  const navigate = useNavigate();
  const primaryBtnRef = useRef(null);

  useEffect(() => {
    if (open && primaryBtnRef.current) {
      // Auto-focus primary action to encourage clicking
      const t = setTimeout(() => primaryBtnRef.current?.focus(), 60);
      return () => clearTimeout(t);
    }
  }, [open]);

  if (!open || !receipt) return null;

  const item = receipt.items?.[0] || {};
  const verifyPath = `/verify/${receipt.nft_id}`;
  const verifyUrl =
    typeof window !== "undefined"
      ? `${window.location.origin}${verifyPath}`
      : verifyPath;

  const copyText = async (text, key, label = "Copied") => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      toast.success(label);
      setTimeout(() => setCopied(null), 1200);
    } catch {
      toast.error("Copy failed");
    }
  };

  const goToCertificate = () => {
    onClose?.();
    navigate(verifyPath);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      data-testid="minted-dialog"
    >
      <div
        className="absolute inset-0 bg-gray-900/40 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden">
        {/* Header */}
        <div className="relative bg-gradient-to-br from-[#0055FF] to-indigo-600 p-6 text-white">
          <button
            onClick={onClose}
            data-testid="minted-close"
            className="absolute top-4 right-4 p-2 rounded-lg hover:bg-white/10"
          >
            <X size={18} />
          </button>
          <div className="flex items-center gap-2 text-xs font-semibold tracking-wider uppercase text-white/80">
            <Sparkles size={12} /> Certificate Issued
          </div>
          <h2 className="font-display font-bold text-2xl mt-2 leading-tight">
            {item.name || "Item"}
          </h2>
          <div className="text-sm text-white/80 mt-1">{receipt.store_name}</div>
          <div className="mt-5 flex items-center gap-3">
            <CheckCircle2 size={18} />
            <div className="text-sm">
              Digital certificate of authenticity created successfully.
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="p-6">
          <div className="space-y-0">
            <Row
              label="Certificate ID"
              testid="nft-id-value"
              value={receipt.nft_id}
              mono
            />
            <Row
              label="Purchase Date"
              value={formatDate(receipt.purchase_date)}
            />
            <Row
              label="Price"
              value={formatCurrency(receipt.total, receipt.currency)}
              mono
            />
            <Row label="Receipt ID" value={receipt.receipt_id} mono />
            <div className="flex items-center justify-between py-2.5 border-b border-gray-100">
              <span className="text-[11px] font-semibold tracking-wider uppercase text-gray-500">
                Verification Transaction
              </span>
              <button
                onClick={() => copyText(receipt.tx_hash, "tx")}
                data-testid="copy-tx-hash"
                className="inline-flex items-center gap-1.5 text-sm font-mono text-gray-900 hover:text-[#0055FF] transition"
              >
                {truncHash(receipt.tx_hash, 10, 8)}
                <Copy size={12} />
                {copied === "tx" && (
                  <span className="text-[10px] text-green-600">copied</span>
                )}
              </button>
            </div>
          </div>

          <div className="mt-5 bg-gray-50 rounded-lg border border-gray-200 p-3">
            <div className="text-[10px] font-semibold tracking-wider uppercase text-gray-500 mb-1">
              Metadata preview
            </div>
            <pre
              data-testid="nft-metadata"
              className="font-mono text-[11px] text-gray-700 whitespace-pre-wrap leading-snug max-h-40 overflow-auto"
            >
{JSON.stringify(receipt.nft_metadata, null, 2)}
            </pre>
          </div>
        </div>

        <div className="border-t border-gray-200 p-4 flex flex-col sm:flex-row items-stretch sm:items-center justify-end gap-2 bg-gray-50/50">
          <button
            onClick={() => copyText(verifyUrl, "link", "Verification link copied")}
            data-testid="copy-verify-link"
            className="inline-flex items-center justify-center gap-2 bg-white text-gray-900 border border-gray-200 text-sm font-semibold px-4 py-2 rounded-lg hover:bg-gray-50 hover:border-gray-300 transition"
          >
            <Link2 size={14} />
            {copied === "link" ? "Copied" : "Copy Verification Link"}
          </button>
          <button
            ref={primaryBtnRef}
            onClick={goToCertificate}
            data-testid="view-certificate-btn"
            className="inline-flex items-center justify-center gap-2 bg-[#0055FF] text-white text-sm font-semibold px-4 py-2 rounded-lg shadow-sm hover:bg-[#0044CC] hover:-translate-y-0.5 transition-all focus:ring-2 focus:ring-[#0055FF] focus:ring-offset-2 outline-none"
          >
            <BadgeCheck size={14} />
            View Verification Certificate
          </button>
        </div>
      </div>
    </div>
  );
}
